import React, { useState } from 'react';
import { Box, Typography, Tabs, Tab, Paper, Grid, Chip } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AccountDetails from '../components/AccountDetails';
import YourPlan from '../components/YourPlan';
import ChatSettings from '../components/ChatSettings';
import { BLOCKED_STATUS_LABEL } from '../utils/blockedStatus';

const Settings = ({ account, onUpdated }) => {
  const [tab, setTab] = useState(0);
  const status = account?.onboardingStatus || 'pending';
  const active = status === 'active' || status === 'otp_verified';
  const blocked = account?.apiStatus === 'blocked';

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxHeight: 'calc(100vh - 64px)', overflow: 'auto' }}>
      <Box sx={{ maxWidth: 900, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 46, height: 46, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #075e54, #0a8a7a)', color: '#fff',
            boxShadow: '0 6px 20px rgba(7,94,84,0.3)',
          }}>
            <SettingsIcon />
          </Box>
          <Box>
            <Typography fontWeight={800} fontSize={24} color="#1e293b">Settings</Typography>
            <Typography variant="body2" color="#64748b" fontSize={13}>Manage your account, WhatsApp connection and plan</Typography>
          </Box>
        </Box>

        {/* Status cards */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Paper className="glass-card" sx={{ p: 2, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WhatsAppIcon sx={{ color: '#25D366', fontSize: 22 }} />
                <Typography fontSize={13} fontWeight={700} color="#1e293b">WhatsApp Connection</Typography>
              </Box>
              <Typography fontSize={15} fontWeight={800} color="#075e54" sx={{ wordBreak: 'break-all' }}>
                {account?.whatsappBusinessNumber || '—'}
              </Typography>
              <Chip size="small" label={blocked ? BLOCKED_STATUS_LABEL : active ? 'Active' : (status || 'pending').replace(/_/g, ' ')}
                sx={{
                  alignSelf: 'flex-start', fontWeight: 700, fontSize: 11, textTransform: 'capitalize',
                  bgcolor: blocked ? '#fee2e2' : active ? '#dcfce7' : '#fef3c7', color: blocked ? '#b91c1c' : active ? '#166534' : '#92400e',
                }} />
              <Typography fontSize={11} color="#94a3b8">Phone ID: {account?.phoneNumberId || '—'}</Typography>
              <Typography fontSize={11} color="#94a3b8">WABA ID: {account?.wabaId || '—'}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper className="glass-card" sx={{ p: 2, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WorkspacePremiumIcon sx={{ color: '#f59e0b', fontSize: 22 }} />
                <Typography fontSize={13} fontWeight={700} color="#1e293b">Your Plan</Typography>
              </Box>
              <Typography fontSize={15} fontWeight={800} color="#1e293b">{account?.planName || 'Free'}</Typography>
              <Typography fontSize={11} color="#94a3b8">Full plan details in the Plan tab below</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper className="glass-card" sx={{ p: 2, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ChatBubbleIcon sx={{ color: '#6366f1', fontSize: 22 }} />
                <Typography fontSize={13} fontWeight={700} color="#1e293b">Activity</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 3, mt: 0.5 }}>
                <Box>
                  <Typography fontSize={20} fontWeight={800} color="#075e54">{account?.conversationCount || 0}</Typography>
                  <Typography fontSize={11} color="#94a3b8">Chats</Typography>
                </Box>
                <Box>
                  <Typography fontSize={20} fontWeight={800} color="#075e54">{account?.campaignCount || 0}</Typography>
                  <Typography fontSize={11} color="#94a3b8">Campaigns</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Main tabs */}
        <Paper className="glass-card" sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}
            sx={{
              borderBottom: '1px solid #e5e7eb', px: 2, pt: 1,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 14, color: '#64748b' },
              '& .Mui-selected': { color: '#075e54 !important' },
              '& .MuiTabs-indicator': { bgcolor: '#075e54' },
            }}>
            <Tab icon={<AccountBoxIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Account Details" />
            <Tab icon={<SmartToyIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Chat Settings" />
            <Tab icon={<WorkspacePremiumIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Your Plan" />
          </Tabs>
          <Box sx={{ p: 3 }}>
            {tab === 0 && <AccountDetails account={account} onUpdated={onUpdated} />}
            {tab === 1 && <ChatSettings account={account} onUpdated={onUpdated} />}
            {tab === 2 && <YourPlan account={account} />}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default Settings;
